"use client";

import { Checkbox, Modal } from "@mantine/core";
import Link from "next/link";
import { type ChangeEvent, useState } from "react";

import {
  Badge,
  Button,
  CheckIcon,
  Group,
  H2,
  Stack,
  Text,
} from "@/components/ui/elements";

interface RequiredRegistrationModalProps {
  opened: boolean;
  onClose: () => void;
  onDoNotShowAgain: (hide: boolean) => void;
  onTemporaryHide: () => void;
  viewer: {
    hasProfile: boolean;
    hasBankAccount: boolean;
    isIdentification: boolean;
  };
}

export function RequiredRegistrationModal({
  opened,
  onClose,
  onDoNotShowAgain,
  onTemporaryHide,
  viewer,
}: RequiredRegistrationModalProps) {
  const [doNotShow, setDoNotShow] = useState(false);

  const handleClose = () => {
    if (doNotShow) {
      onDoNotShowAgain(true);
    } else {
      // 「今後表示しない」がチェックされていない場合は24時間非表示
      onTemporaryHide();
    }
    onClose();
  };

  const handleRegisterClick = () => {
    // 登録ボタンをクリックした場合も24時間非表示
    onTemporaryHide();
    onClose();
  };

  // ロード中または表示条件を満たさない場合は何も表示しない
  if (!opened || !viewer) {
    return null;
  }

  const { hasProfile, hasBankAccount, isIdentification } = viewer;

  const registrationItems = [
    {
      label: "プロフィール登録",
      completed: hasProfile,
      description: "お名前、住所、業務経験等",
    },
    {
      label: "本人確認書類の提出",
      completed: isIdentification,
      description: "身分証明書のアップロード",
    },
    {
      label: "銀行口座の登録",
      completed: hasBankAccount,
      description: "報酬受取用の口座情報",
    },
  ];

  const incompleteItems = registrationItems.filter((item) => !item.completed);
  const allCompleted = incompleteItems.length === 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={null}
      centered
      size="md"
      padding="lg"
    >
      <Stack gap="lg">
        <Stack gap="sm" align="center">
          <H2 c="orange">応募に必要な登録</H2>
          <Text ta="center" c="dimmed">
            応募するには以下の登録が必要です。
          </Text>
        </Stack>

        <Stack gap="md">
          {registrationItems.map((item, index) => (
            <Group
              key={index}
              justify="space-between"
              p="md"
              style={{
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                backgroundColor: item.completed ? "#f8f9fa" : "#fff",
              }}
            >
              <Group gap="md">
                {item.completed ? (
                  <CheckIcon color="green" size={20} />
                ) : (
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#fd7e14",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </div>
                )}
                <Stack gap={2}>
                  <Text fw="bold" size="sm">
                    {item.label}
                  </Text>
                  <Text c="dimmed" size="xs">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
              {item.completed ? (
                <Badge color="green" variant="filled" size="sm">
                  完了
                </Badge>
              ) : (
                <Text c="orange" fw="bold" size="xs">
                  未登録
                </Text>
              )}
            </Group>
          ))}
        </Stack>

        {!allCompleted && (
          <Stack gap="md">
            <Button
              component={Link}
              href="/registration-guide"
              color="orange"
              size="lg"
              fullWidth
              onClick={handleRegisterClick}
            >
              登録する
            </Button>
            <Text ta="center" c="dimmed" size="sm">
              ガイドに沿って順番に登録できます
            </Text>
          </Stack>
        )}

        {incompleteItems.length > 0 && (
          <Checkbox
            label="今後表示しない"
            checked={doNotShow}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setDoNotShow(event.currentTarget.checked)
            }
            size="sm"
          />
        )}

        <Group justify="center" gap="md">
          <Button
            variant="outline"
            color="gray"
            onClick={handleClose}
            size="md"
          >
            {allCompleted ? "閉じる" : "後で登録する"}
          </Button>
          {allCompleted && (
            <Button color="green" onClick={handleClose} size="md">
              応募準備完了
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
